import { untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { WizardComponent, WizardMarkableActions, WizardStep, WizardStepMarks, WizardStepQueue } from 'mbs-ui-kit';
import { NotificationPerPlanSettings } from 'mbs-ui-shared@models/Notification';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RecurType } from 'mbs-ui-shared@models/RecurType.enum';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { first, ObservedValueOf } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { StepsDataFromPlanHelper } from 'wizards/helpers/steps-data-from-plan-helper/steps-data-from-plan-helper';
import { SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { RestoreTypes } from 'wizards/models/base/restore-types-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';

type WizardStepsNames =
  | 'advancedOptions'
  | 'advancedSettings'
  | 'compressionEncryption'
  | 'consistency'
  | 'encryptionOption'
  | 'retention'
  | 'schedule'
  | 'selectVM';

export interface WizardStepsManagerHook {
  isNBF?: boolean;
  isEdit?: boolean;
  isNetBuild?: boolean;
  isUnix?: boolean;
  isRDMode?: boolean;
  planType?: PlanTypes;
  FormModel: Record<string, Record<string, any>>;
  ability: AbilityService;
  wizard: WizardComponent;
  ignorableSteps?: Array<string>;
  forceValidStep(name: string): void;
}

export class WizardStepsManagerClass {
  public titles: Record<WizardStepsNames, string> = {
    advancedOptions: this.i18nPipe.transform('wizards:advanced_options_title'),
    advancedSettings: this.i18nPipe.transform('wizards:advanced_settings_title'),
    compressionEncryption: this.i18nPipe.transform('wizards:compression_encryption_step_title'),
    consistency: this.i18nPipe.transform('wizards:archive_consistency_title'),
    encryptionOption: this.i18nPipe.transform('wizards:encryption_options_step_title'),
    retention: this.i18nPipe.transform('wizards:retention_policy_title'),
    schedule: this.i18nPipe.transform('wizards:schedule_title'),
    selectVM: this.i18nPipe.transform('wizards:what_backup_step_title')
  };
  public allStepsVisited = false;
  public stepQueue: WizardStepQueue = new WizardStepQueue();
  public stepMarks: WizardStepMarks = new WizardStepMarks();
  public loadingSteps: boolean[] = [];

  private readonly notFullScheduleTypes = [...RestoreTypes, PlanTypes.DataBasePlan, PlanTypes.ConsistencyCheck];
  private _visitedSteps: Array<string> = [];
  private _currentStep: WizardStep;
  private mainService: RemoteManagementWizardsService;
  private scheduledFromPlanAdded = false;
  protected ABTest: ObservedValueOf<ReturnType<typeof this.stepService.pipeABTest>> = {};

  public set currentStep(currentStep: WizardStep) {
    this._currentStep = currentStep;
  }
  public get currentStep(): WizardStep {
    return this._currentStep;
  }
  public get useNewSchedule(): boolean {
    const allowedOS = !this.hooks.isUnix || this.hooks.isNetBuild;
    const rightVersion = this.mainService?.backupVersionUpdated && +this.mainService?.backupVersionUpdated.substring(0, 2) >= 80;

    return rightVersion && allowedOS && this.hooks.isNBF && !this.hooks.isRDMode;
  }
  public get useNewRetention(): boolean {
    const allowedOS = !this.hooks.isUnix || this.hooks.isNetBuild;
    const rightVersion = this.mainService?.backupVersionUpdated && +this.mainService?.backupVersionUpdated.substring(0, 2) >= 81;

    return rightVersion && allowedOS && this.hooks.isNBF && !this.hooks.isRDMode;
  }
  get backupVersionUpdated(): string {
    return this.mainService.backupVersionUpdated;
  }
  public get advancedOptionsNeedShow(): boolean {
    return SupportMethodsForStepsFromPlan.advancedOptionsNeedShow(this.hooks.FormModel.advancedOptionsFlags, this.hooks.planType);
  }

  constructor(
    private i18nPipe: I18NextPipe,
    private stepService: WizardStepsService,
    private hooks: WizardStepsManagerHook
  ) {}

  setMainService(mainService: RemoteManagementWizardsService) {
    this.mainService = mainService;

    this.stepService
      .pipeProperty('ABTest')
      .pipe(startWith(this.stepService.getSnapshot('ABTest')), untilDestroyed(this.hooks))
      .subscribe((ABTest) => {
        if (ABTest.DestinationPlusRetentionStep) {
          this.titles = { ...this.titles, retention: this.i18nPipe.transform('wizards:whereBackup.where_backup_step_title') };
        }
      });
  }

  setScheduledFromPlanAdded(value: boolean): void {
    this.scheduledFromPlanAdded = value;
  }

  setCurrentStep(stepName: string, forceValid = false) {
    const step = this.hooks.wizard.steps.find((step) => step.title === stepName);

    if (step) {
      if (forceValid) {
        this.hooks.wizard.currentStep.valid = true;
      }

      this.hooks.wizard.currentStep = step;
    }
  }

  next(): void {
    this.hooks.wizard.changeStep(1);
  }

  back(): void {
    this.hooks.wizard.changeStep(-1);
  }

  forceNextStepWizard(isSave: boolean, name: string, title: string): void {
    if (!this.hooks.wizard.steps) {
      return;
    }

    if (isSave) {
      this.forceValidStep(name);
      this.hooks.wizard.handleSave();

      return;
    }

    const stepsArray = this.hooks.wizard.steps.toArray();
    const currentStepIndex = stepsArray.findIndex((s) => s.title === title);
    const nextStepIndex = stepsArray.findIndex((s) => s.title === this.currentStep.title);

    if (nextStepIndex !== -1 && nextStepIndex === currentStepIndex) {
      this.forceValidStep(name);
      this.next();

      return;
    }

    this.forceValidStep(name);
  }

  forceBackStepWizard(name: string, title: string): void {
    if (this.hooks.wizard.steps) {
      const stepsArray = this.hooks.wizard.steps.toArray();
      const currentStepIndex = stepsArray.findIndex((s) => s.title === title);
      const nextStepIndex = stepsArray.findIndex((s) => s.title === this.currentStep.title);

      if (nextStepIndex !== -1 && nextStepIndex === currentStepIndex) {
        this.back();
        this.hooks.wizard.currentStep = stepsArray[nextStepIndex - 1];
      }
    }
  }

  forceValidStep(name: string): void {
    this.hooks.forceValidStep(name);
    this.hooks.wizard.currentStep.valid = true;
  }

  processLoadingStep(loading: boolean): boolean {
    if (loading) {
      this.loadingSteps.push(true);
    } else {
      this.loadingSteps.shift();
    }

    return !!this.loadingSteps.length;
  }

  registerVisitedStep(step?: WizardStep, fromUser = false): void {
    const stepInstance = step || this.hooks?.wizard?.currentStep;

    if (!stepInstance) return;

    if (!this._visitedSteps.includes(stepInstance.title)) {
      this._visitedSteps.push(stepInstance.title);
    }

    const stepsToCheck = this.hooks?.wizard?.steps
      .toArray()
      .filter((stepInstance) => !(this.hooks.ignorableSteps || []).includes(stepInstance.title));
    const hasUnVisitedSteps = stepsToCheck.some((stepInstance) => !this._visitedSteps.includes(stepInstance.title));

    this.allStepsVisited = !hasUnVisitedSteps;

    if (fromUser) {
      this.registerUserInteractWithSteps();
    }
  }

  registerUserInteractWithSteps(): void {
    this.stepService.updateProperty('wizard', 'userInteractWithSteps', true);
  }

  addStepMark(event: { stepName: string; actions: WizardMarkableActions }): void {
    if (!event || !event.stepName || !event.actions) {
      return;
    }

    this.stepMarks.addMark(event.stepName, event.actions);
  }

  removeStepMark(event: { stepName: string; actions: Array<keyof WizardMarkableActions> }): void {
    if (!event || !event.stepName || !event.actions) {
      return;
    }

    this.stepMarks.removeMark(event.stepName, event.actions);
  }

  showEncryptionOptionsStepIfNeed(): void {
    const form = this.mainService.initialFormData;
    if (!this.hooks.isEdit && form) {
      setTimeout(() =>
        Object.keys(form).forEach((key) => {
          if (key === 'typeDataStep') {
            this.forceNextStepWizard(false, key, this.currentStep.title);
            this.forceNextStepWizard(false, 'partitionsStep', this.currentStep.title);
          } else if (key !== 'encryptionOptionsStep') this.forceNextStepWizard(false, key, this.currentStep.title);
        })
      );
    }
  }

  setDefaultPlanName(): void {
    if (this.hooks.planType !== PlanTypes.ConsistencyCheck) {
      this.hooks.FormModel.planNameStep.Name = SupportMethodsForStepsFromPlan.generatePlanName(
        this.mainService.compName,
        this.hooks.planType,
        this.mainService.timeZoneOffset
      );
    }
  }

  setScheduleRestoreOnce(event: boolean): void {
    if (event) this.hooks.FormModel.scheduleStep.ScheduleType = 'noschedule';
  }

  setHybridToDefault(event: boolean): void {
    if (
      event &&
      (!this.hooks.isUnix || this.hooks.isNetBuild) &&
      this.hooks.FormModel.advancedOptionsStep &&
      !(
        this.hooks.FormModel.advancedOptionsStep.SyntheticFull === null ||
        this.hooks.FormModel.advancedOptionsStep.SyntheticFull === undefined
      )
    )
      this.hooks.FormModel.advancedOptionsStep.SyntheticFull = true;
  }

  setAdvancedOptionsFlagAfterChangeStorage(): void {
    if (this.hooks.FormModel.advancedOptionsFlags) {
      this.hooks.FormModel.advancedOptionsFlags = {
        ...this.hooks.FormModel.advancedOptionsFlags,
        UseS3AccelerationPossible: false,
        StorageClassSettingPossible: false,
        AzureAccessTierType: false,
        SyntheticFullSettingPossible: false
      };
    }
  }

  getCloudOrLocalConnectionId(): StorageConnection {
    return (
      this.hooks.FormModel.Storages.LocalStorages.find((s) => s.ID === this.hooks.FormModel.whereBackupStep.ConnectionID) ||
      this.hooks.FormModel.Storages.CloudStorages.find((s) => s.ID === this.hooks.FormModel.whereBackupStep.ConnectionID)
    );
  }

  setAdvancedOptionsFlags(currentStorage: StorageConnection): void {
    const whereBackupStep = this.hooks.FormModel.whereBackupStep;
    const cloudStorages = this.hooks.FormModel.Storages?.CloudStorages;
    // FormModel.Storages may not contain a just-created destination yet (MBS-32530: the list arrives
    // with the next load wave), while the selected connection itself already carries the capability
    // fields - so it backs up the lookup, otherwise synthetic gets cut off by a stale list
    const cloudStorage =
      cloudStorages?.find((i) => i.ID === whereBackupStep?.ConnectionID) ?? (currentStorage?.IsLocal ? null : currentStorage);

    this.hooks.FormModel.advancedOptionsFlags = SupportMethodsForStepsFromPlan.getStorageAdvancedOptionsFlags(
      this.hooks.FormModel.advancedOptionsFlags,
      {
        agentVersion: this.mainService.backupVersionUpdated,
        cloudStorage,
        isNBF: this.hooks.isNBF,
        isNetBuild: this.hooks.isNetBuild,
        isUnix: this.hooks.isUnix,
        storage: currentStorage,
        updateSynthetic: !!whereBackupStep && !whereBackupStep.IsHybridBackup
      }
    );
  }

  updateAdvancedSchedule(plan, loadedPlan): void {
    let validSchedule: boolean;

    if (this.notFullScheduleTypes.includes(this.hooks.planType)) {
      validSchedule = plan?.Schedule?.Enabled && !isNil(plan.Schedule.Hour);
    } else {
      validSchedule = plan?.ForceFullSchedule?.Enabled && !isNil(plan.ForceFullSchedule.Hour);
    }

    if (this.hooks.isEdit && !this.scheduledFromPlanAdded && validSchedule) {
      this.hooks.FormModel.scheduleAdvancedStep = StepsDataFromPlanHelper.updateAdvancedScheduleBasedOnMode(
        loadedPlan.plan || loadedPlan.Plan,
        !this.hooks.isEdit,
        this.hooks.planType,
        this.hooks.isUnix && !this.hooks.isNetBuild
      );
      this.scheduledFromPlanAdded = true;
    } else
      this.hooks.FormModel.scheduleAdvancedStep = StepsDataFromPlanHelper.advancedScheduleDefault(
        this.hooks.planType,
        this.hooks.isUnix && !this.hooks.isNetBuild
      );
  }

  updateSimpleScheduleStep(plan, loadedPlan): void {
    if (
      this.hooks.isEdit &&
      (!this.hooks.isUnix || this.hooks.isNetBuild) &&
      (plan?.Schedule?.RecurType === RecurType.Periodically || plan?.ForceFullSchedule?.RecurType === RecurType.Periodically)
    ) {
      this.updateAdvancedSchedule(plan, loadedPlan);
      this.hooks.FormModel.simpleScheduleStep = StepsDataFromPlanHelper.updateSimpleSchedule(plan, this.hooks.planType);
    }
  }

  updateDestinationAsDefault(): void {
    if (this.mainService.type === PlanTypes.RestorePlan) {
      this.mainService.updateDestinationAsDefault(
        this.hooks.FormModel.destinationStep.destinationFolder,
        this.hooks.FormModel.destinationStep.saveLocationAsDefault
      );
    }
  }

  updateOverdueAlert(newPlan, notification: NotificationPerPlanSettings, hid: string = null): void {
    if (this.hooks.ability.can('read', 'Monitoring')) {
      const overdue = {
        enabled: this.hooks.FormModel.scheduleStep.overdueAfterSuccessEnabled,
        amount: this.hooks.FormModel.scheduleStep.overdueAfterSuccessAmount || 7,
        period: !(
          this.hooks.FormModel.scheduleStep.overdueAfterSuccessPeriod === null ||
          this.hooks.FormModel.scheduleStep.overdueAfterSuccessPeriod === undefined
        )
          ? this.hooks.FormModel.scheduleStep.overdueAfterSuccessPeriod
          : 1
      };

      this.mainService
        .updateOverdueAlert(overdue, newPlan.Name)
        .pipe(first())
        .subscribe({
          next: (result) => {
            if (result && (this.mainService.isProvider || this.hooks.ability.can('read', 'Notification'))) {
              this.mainService.SavePlanNotification(notification, hid).pipe(first()).subscribe();
            }
          },
          error: () => console.error('Plan overdue settings has not been saved. Something went wrong.')
        });
    } else this.mainService.SavePlanNotification(notification, hid).pipe(first()).subscribe();
  }
}
